import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Chip, MenuItem, Tab, Tabs, TextField, Typography } from '@mui/material';
import useExpenseLookups from '../../components/expense/useExpenseLookups';
import { formatDate, formatDateTime } from '../../services/expenseVouchersApi';
import { formatMoney, reportsApi, WALLET_TYPE_LABELS } from '../../services/expenseAccountsApi';
import { DateRangeFilter, EmployeeFilter } from '../../components/expense/AccountsFilters';
import {
  CurrencyTotals, ExportButton, FilterBar, ReportsHeader, ReportTable, useReport,
} from '../../components/expense/ReportShared';

const signed = (amount, currency) => {
  const text = formatMoney(amount, currency);
  return <Typography variant="body2" sx={{ fontWeight: 600, color: amount < 0 ? 'error.main' : 'success.main' }}>{amount > 0 ? `+${text}` : text}</Typography>;
};

// Positive = the employee holds company money (recoverable); negative = owed to the employee
const POSITION_META = {
  RECOVERABLE: { label: 'Recoverable from employee', color: 'success' },
  PAYABLE: { label: 'Payable to employee', color: 'error' },
  ZERO: { label: 'Settled', color: 'default' },
};

const CurrencySelect = ({ currencies, value, onChange }) => (
  <TextField select size="small" label="Currency" value={value} onChange={onChange} sx={{ minWidth: 130 }}>
    <MenuItem value="">All</MenuItem>
    {currencies.map((c) => <MenuItem key={c.value} value={c.value}>{c.value}</MenuItem>)}
  </TextField>
);

const BalancesView = ({ users, currencies, onOpenLedger }) => {
  const report = useReport(reportsApi.walletBalances, { emptyFilters: { employeeId: '', currency: '', asOf: '' }, defaultSort: { sort: 'employee', order: 'asc' } });
  const { filters, setFilter, setFilterValue } = report;
  const columns = [
    { label: 'Employee', sortKey: 'employee', render: (r) => <>{r.employeeName}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.employeeCode || r.employeeEmail}</Typography></> },
    { label: 'Currency', sortKey: 'currency', nowrap: true, render: (r) => r.currency },
    { label: 'Current Balance', sortKey: 'balance', nowrap: true, align: 'right', render: (r) => <Typography variant="body2" sx={{ fontWeight: 600, color: r.balance < 0 ? 'error.main' : undefined }}>{formatMoney(r.balance, r.currency)}</Typography> },
    { label: 'Position', render: (r) => <Chip size="small" variant="outlined" color={POSITION_META[r.position]?.color} label={POSITION_META[r.position]?.label || r.position} /> },
    { label: 'Last Transaction', sortKey: 'lastTransactionAt', nowrap: true, render: (r) => formatDate(r.lastTransactionAt) },
    { label: 'Ledger Entries', sortKey: 'entries', align: 'right', render: (r) => r.entries },
  ];
  return (
    <>
      <FilterBar onClear={report.clear}>
        <TextField size="small" placeholder="Search employee or currency..." value={report.search} onChange={(e) => report.setSearch(e.target.value)} sx={{ minWidth: 240 }} />
        <EmployeeFilter users={users} value={filters.employeeId} onChange={setFilterValue('employeeId')} />
        <CurrencySelect currencies={currencies} value={filters.currency} onChange={setFilter('currency')} />
        <TextField size="small" type="date" label="Balance as of" value={filters.asOf} onChange={setFilter('asOf')} InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Balance as of' }} sx={{ width: 170 }} />
        <ExportButton onExport={reportsApi.exportWalletBalances} params={report.params} />
      </FilterBar>
      <CurrencyTotals
        testId="wallet-totals"
        summary={report.summary}
        title={(s) => `${s.currency} - ${s.employees} employee wallet${s.employees === 1 ? '' : 's'}`}
        lines={(s) => [
          `Recoverable from employees ${formatMoney(s.recoverable, s.currency)} (${s.recoverableEmployees})`,
          `Payable to employees ${formatMoney(s.payable, s.currency)} (${s.payableEmployees})`,
          `Net ${formatMoney(s.balance, s.currency)}`,
        ]}
      />
      <ReportTable report={report} columns={columns} rowKey={(r) => r._id} emptyText="No wallet balances." onRowClick={onOpenLedger} />
    </>
  );
};

const LedgerView = ({ users, currencies, initialFilters }) => {
  const empty = { employeeId: '', currency: '', type: '', from: '', to: '' };
  const report = useReport(reportsApi.walletLedger, { emptyFilters: empty, initialFilters: { ...empty, ...initialFilters }, defaultSort: { sort: 'at', order: 'desc' } });
  const { filters, setFilter, setFilterValue } = report;
  const columns = [
    { label: 'Date', sortKey: 'at', nowrap: true, render: (r) => formatDateTime(r.at) },
    { label: 'Employee', render: (r) => r.employeeName || '-' },
    { label: 'Currency', sortKey: 'currency', nowrap: true, render: (r) => r.currency },
    { label: 'Requested', nowrap: true, align: 'right', render: (r) => (r.requestedAmount === null ? '-' : formatMoney(r.requestedAmount, r.currency)) },
    { label: 'Credited', nowrap: true, align: 'right', render: (r) => (r.creditedAmount === null ? '-' : formatMoney(r.creditedAmount, r.currency)) },
    { label: 'Wallet Movement', sortKey: 'amount', nowrap: true, align: 'right', render: (r) => signed(r.amount, r.currency) },
    { label: 'Balance After', nowrap: true, align: 'right', render: (r) => formatMoney(r.balanceAfter, r.currency) },
    { label: 'Type', sortKey: 'type', render: (r) => <Chip size="small" variant="outlined" color={r.type === 'REVERSAL' ? 'error' : 'default'} label={WALLET_TYPE_LABELS[r.type] || r.type} /> },
    { label: 'Reference Voucher', nowrap: true, render: (r) => r.voucherNumber || '-' },
    {
      label: 'Reversal',
      render: (r) => {
        if (r.reversalOf) return `Reverses ${WALLET_TYPE_LABELS[r.reversalOfType] || 'entry'} of ${formatDate(r.reversalOfAt)}`;
        if (r.reversedBy) return `Reversed on ${formatDate(r.reversedAt)}`;
        return '-';
      },
    },
    { label: 'Narration', render: (r) => r.comment || '-' },
    { label: 'Created By', render: (r) => r.byName || '-' },
  ];
  return (
    <>
      <FilterBar onClear={report.clear}>
        <TextField size="small" placeholder="Search employee, voucher, narration..." value={report.search} onChange={(e) => report.setSearch(e.target.value)} sx={{ minWidth: 260 }} />
        <EmployeeFilter users={users} value={filters.employeeId} onChange={setFilterValue('employeeId')} />
        <CurrencySelect currencies={currencies} value={filters.currency} onChange={setFilter('currency')} />
        <TextField select size="small" label="Transaction type" value={filters.type} onChange={setFilter('type')} sx={{ minWidth: 190 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(WALLET_TYPE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
        <DateRangeFilter label="Date" from={filters.from} to={filters.to} onFromChange={setFilterValue('from')} onToChange={setFilterValue('to')} />
        <ExportButton onExport={reportsApi.exportWalletLedger} params={report.params} />
      </FilterBar>
      <ReportTable report={report} columns={columns} emptyText="No ledger entries." />
    </>
  );
};

// Wallet Balances report: one row per employee and currency (never combined), and the
// full ledger of every wallet. Clicking a balance opens its ledger.
const AccountsWalletReport = () => {
  const { currencies, users } = useExpenseLookups(['currencies', 'users']);
  // ?employeeId=&currency= (e.g. from the Advances report) opens that wallet's ledger
  const [searchParams] = useSearchParams();
  const linked = searchParams.get('employeeId') ? { employeeId: searchParams.get('employeeId'), currency: searchParams.get('currency') || '' } : null;
  const [view, setView] = useState(linked ? 'ledger' : 'balances');
  const [ledgerFilters, setLedgerFilters] = useState(linked || {});
  const openLedger = (row) => {
    setLedgerFilters({ employeeId: String(row.employee), currency: row.currency });
    setView('ledger');
  };
  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <ReportsHeader title="Wallet Balances" subtitle="Balances per employee and currency, from the wallet ledger. Positive = recoverable from the employee, negative = payable to the employee. Currencies are never combined." />
      <Tabs value={view} onChange={(e, v) => { setLedgerFilters({}); setView(v); }} sx={{ mb: 2 }} aria-label="Wallet report views">
        <Tab value="balances" label="Balances" sx={{ textTransform: 'none' }} />
        <Tab value="ledger" label="Ledger" sx={{ textTransform: 'none' }} />
      </Tabs>
      {view === 'balances'
        ? <BalancesView users={users} currencies={currencies} onOpenLedger={openLedger} />
        : <LedgerView key={JSON.stringify(ledgerFilters)} users={users} currencies={currencies} initialFilters={ledgerFilters} />}
    </Box>
  );
};

export default AccountsWalletReport;
